/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef
} from 'react';
import {IconButton} from '../../components/buttons/IconButton';
import {ImportIcon} from '../../icons/ImportIcon';
import {dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';

// defines the file browse button props ...
export type FileBrowseButtonProps = {
  onImport: (file: FileList) => void;
  entity: string;
  fileTypes: string;
  size?: number;
  disabled?: boolean;
};

/**
 * Defines the Design Language System (DLS) File Browse Button. This component handles
 * the file browse functionality.
 *
 * ```typescript
 *
 *   import {FileBrowseButton} from '@ion/dls/src/editors/controls/FileBrowseButton';
 *
 *   <FileBrowseButton onImport={onImport}
 *     entity={entity}
 *     fileTypes='.csv, .xls, .xlsx' />
 * ```
 *
 */
export const FileBrowseButton = ({onImport, entity, fileTypes, size, disabled = false}: FileBrowseButtonProps) => {
  const id        = `${entity}-import`;
  const buttonRef = useRef(document.getElementById(id));

  useEffect(() => {
    buttonRef.current = document.getElementById(id);
  }, []);

  const onUpload = useCallback((e) => {
    const {
      target: {
        files
      },
      value
    } = e || {};
    if (files?.length && !value) {
      onImport(files);
    }
  }, [onImport]);

  const onIconClick = useCallback(() => {
    buttonRef?.current?.click();
  }, []);

  const onInputClick = useCallback((e) => {
    e.stopPropagation();
    e.target.value = null;
  }, []);

  return (
    <>
      <input id={id} type='file' style={{display: 'none'}}
        accept={fileTypes}
        onInput={onUpload} onClick={onInputClick} />
      <label htmlFor={id}>
        <IconButton color={disabled ? dlsColor('grey5') : dlsColor('aaBlue')} icon={<ImportIcon size={size || IconSize.extraLarge} />}
          onClick={onIconClick} />
      </label>
    </>
  );
};

export default FileBrowseButton;
